import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '~/Locale';
import { CarRaceEngine, GameResult, HudState } from './carRaceEngine';
import {
  COLORS,
  GAME_CANVAS_HEIGHT,
  GAME_CANVAS_WIDTH,
  START_LIVES
} from './constants';
import Hud from './components/Hud';
import StartOverlay from './components/StartOverlay';
import GameOverOverlay from './components/GameOverOverlay';
import MobileControls from './components/MobileControls';

type Phase = 'start' | 'playing' | 'gameover';

const Outer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${GAME_CANVAS_WIDTH}px;
  border: 3px solid ${COLORS.yellow};
  border-radius: 16px;
  overflow: hidden;
  background: ${COLORS.black};
  font-family: 'Open Sans', sans-serif;
  user-select: none;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: ${COLORS.black};
`;

const Wordmark = styled.span`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 1px;
  color: ${COLORS.yellow};
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  line-height: 0;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: auto;
  touch-action: none;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: rgba(26, 32, 48, 0.95);
  z-index: 10;
  line-height: 1.4;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  background: ${COLORS.black};
`;

const SpeedWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpeedLabel = styled.span`
  font-size: 10px;
  color: #888;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SpeedBar = styled.div`
  width: 100px;
  height: 8px;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
`;

const SpeedFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${COLORS.teal}, ${COLORS.yellow});
  border-radius: 4px;
  transition: width 0.3s;
`;

const Hint = styled.span`
  font-size: 10px;
  color: #666;

  /* Hide the keyboard hint on touch devices (mobile controls replace it). */
  @media (hover: none) and (pointer: coarse) {
    display: none;
  }
`;

const CarRaceGame = () => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<CarRaceEngine | null>(null);

  const [phase, setPhase] = useState<Phase>('start');
  const [hud, setHud] = useState<HudState>({
    score: 0,
    deals: 0,
    lives: START_LIVES,
    speedPct: 0
  });
  const [best, setBest] = useState(0);
  const [result, setResult] = useState<GameResult | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const engine = new CarRaceEngine(canvas, {
      onHud: setHud,
      onGameOver: gameResult => {
        setResult(gameResult);
        setBest(gameResult.best);
        setPhase('gameover');
      }
    });
    engineRef.current = engine;
    setBest(engine.getBest());

    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  const handleStart = useCallback(() => {
    engineRef.current?.start();
    setResult(null);
    setPhase('playing');
  }, []);

  return (
    <Outer>
      <Wrapper data-testid="car-race-game">
        <Header>
          <Wordmark>KVDBIL</Wordmark>
          <Hud
            score={hud.score}
            deals={hud.deals}
            lives={hud.lives}
            best={best}
          />
        </Header>

        <Stage>
          <Canvas
            ref={canvasRef}
            width={GAME_CANVAS_WIDTH}
            height={GAME_CANVAS_HEIGHT}
          />
          {phase === 'start' && (
            <Overlay>
              <StartOverlay onStart={handleStart} />
            </Overlay>
          )}
          {phase === 'gameover' && result && (
            <Overlay>
              <GameOverOverlay result={result} onRestart={handleStart} />
            </Overlay>
          )}
        </Stage>

        <Footer>
          <SpeedWrap>
            <SpeedLabel>{t('Speed')}</SpeedLabel>
            <SpeedBar>
              <SpeedFill style={{ width: `${10 + hud.speedPct * 90}%` }} />
            </SpeedBar>
          </SpeedWrap>
          <Hint>{t('← → or A D')}</Hint>
        </Footer>

        <MobileControls
          onLeft={() => engineRef.current?.moveLeft()}
          onRight={() => engineRef.current?.moveRight()}
        />
      </Wrapper>
    </Outer>
  );
};

export default CarRaceGame;
